/* =====================================================================
   Meluya Upsell-Produkt-Block (Phase 1, standalone)

   Custom Element <meluya-upsell-product>. Einzelner Block, der ein
   Add-on-Produkt unter dem Buy-Button-Bereich der PDP zeigt. Der Block
   hängt sich auf den Submit des Haupt-Produkt-Forms: bei aktivem
   Toggle wird zuerst die Upsell-Variante via /cart/add.js hinzugefügt,
   danach wird das Original-Form re-submitted, sodass Aurora's eigene
   <product-form>-Komponente alles weitere übernimmt (Cart-API-Call,
   Drawer/Popup-Refresh, je nach Shop-Setup).

   Bewusst KEIN eigener Drawer-Code: nach erfolgreichem Upsell-Add wird
   das Haupt-Form ganz normal abgeschickt. Was Aurora nach dem Submit
   tut (Drawer öffnen, Notification-Popup, Cart-Page-Redirect) bleibt
   unverändert.

   Phase-2-Vorbereitung:
   - State-/Component-ID gekapselt im Custom Element (kein window.*)
   - buildAddItemPayload() zentralisiert das Cart-Add-Payload, damit
     später bei Bedarf Kaching-Bundle-Properties injiziert werden
     können, ohne die Submit-Logik zu zerlegen.
   - Custom Element funktioniert auch verschachtelt im Set-Selector:
     IDs sind unique pro {upsellId, sectionId, blockInstance}, Submit-
     Hook filtert nach Form-ID, kein globaler State.
===================================================================== */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('meluya-upsell-product')) {
    return;
  }

  /* Diagnose-Mode: aktiviert sich automatisch, wenn die URL den Query-
     Parameter ?mup_debug=1 trägt, oder wenn localStorage.mupDebug='1'
     gesetzt ist. Alle wichtigen Submit-Pfad-Schritte loggen dann mit
     Prefix [meluya-upsell] in die Browser-Konsole, damit Marc
     reproduzieren und den Pfad einsehen kann, ohne dass Live-User
     irgendwas davon mitbekommen. */
  const DEBUG = (function () {
    try {
      if (location && location.search && location.search.indexOf('mup_debug=1') >= 0) return true;
      if (window.localStorage && window.localStorage.getItem('mupDebug') === '1') return true;
    } catch (e) {
      /* ignore (privacy mode etc) */
    }
    return false;
  })();
  const log = DEBUG
    ? function () {
        try {
          console.log.apply(console, ['[meluya-upsell]'].concat(Array.prototype.slice.call(arguments)));
        } catch (e) { /* ignore */ }
      }
    : function () {};

  const parseJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return null;
    }
  };

  /* Aurora's Produkt-Form enthält ein hidden <input name="id"> für die
     Variant-ID. HTMLFormElement's named-element getter shadowt damit die
     Element.id-Property: form.id liefert dann das Input-DOM-Node statt
     dem String. Über getAttribute kommen wir am Getter vorbei. */
  const getFormId = (form) => (form ? form.getAttribute('id') : null);

  class MeluyaUpsellProduct extends HTMLElement {
    constructor() {
      super();
      this._onSubmit = this._onSubmit.bind(this);
      this._onSubmitterClick = this._onSubmitterClick.bind(this);
    }

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this._productFormId = this.dataset.productFormId;
      this._upsellProductId = this.dataset.upsellProductId;
      this._sectionId = this.dataset.sectionId;

      log('connected', {
        productFormId: this._productFormId,
        upsellProductId: this._upsellProductId,
        sectionId: this._sectionId,
        formExists: !!document.getElementById(this._productFormId),
      });

      this._defaultActive = this.dataset.defaultActive === 'true';
      this._autoSelectFirst = this.dataset.autoSelectFirst === 'true';
      this._priceMode = this.dataset.priceDisplayMode || 'show_full';
      this._isSingleVariant = this.dataset.singleVariant === 'true';

      // Multi-slot Konfiguration. max_quantity = wieviele Stück der Kunde
      // maximal kaufen kann (gerendert sind so viele Slot-Cards), default
      // ist die initiale activeQuantity bevor das Variant-Mapping greift.
      const rawMax = parseInt(this.dataset.maxQuantity, 10);
      this._maxQuantity = Number.isFinite(rawMax) && rawMax > 0
        ? Math.min(10, rawMax)
        : 1;
      const rawDefault = parseInt(this.dataset.defaultQuantity, 10);
      this._defaultQuantity = Number.isFinite(rawDefault) && rawDefault > 0
        ? Math.min(this._maxQuantity, rawDefault)
        : 1;

      const primaryRaw = this.getAttribute('data-primary-option-name') || 'farbe,color';
      this._primaryOptionNames = primaryRaw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);

      this._variants = parseJSON(this.querySelector('[data-mup-variants]')) || [];
      this._optionNames = parseJSON(this.querySelector('[data-mup-option-names]')) || [];
      this._labels = parseJSON(this.querySelector('[data-mup-labels]')) || {};
      this._defaultMapping = parseJSON(this.querySelector('[data-mup-default-mapping]')) || [];
      // Hauptprodukt-Variants als Lookup für das Default-Mengen-Mapping.
      // Gerendert vom Snippet aus product.variants, sodass wir bei einem
      // Variant-Wechsel die option-Werte zur aktuellen Variante kennen,
      // ohne im DOM nach Aurora's Variant-Picker-Pills suchen zu müssen
      // (deren Markup variiert und ist nicht zuverlässig parsebar).
      this._mainVariants = parseJSON(this.querySelector('[data-mup-main-variants]')) || [];

      // Discount-Setup: dieselben 30 % Hard-Cap wie im Function-Code
      // (MAX_DISCOUNT_PERCENT). Wird beides geändert, müssen beide Stellen
      // mit. Client-side Clamp ist nur fürs UI; die Function clampt server-
      // seitig nochmal, damit Manipulation der Properties im Browser nicht
      // mehr Rabatt rauskitzelt.
      this._discountActive = this._labels.discountActive === true;
      const rawPct = parseInt(this._labels.discountPercent, 10);
      this._discountPercent = Number.isFinite(rawPct)
        ? Math.max(0, Math.min(30, rawPct))
        : 0;
      this._showStrikethrough = this._labels.showStrikethroughPrice !== false;

      this._row = this.querySelector('[data-mup-row]');
      this._toggle = this.querySelector('[data-mup-toggle]');
      this._previewImg = this.querySelector('[data-mup-preview-img]');
      this._previewFallback = this.querySelector('[data-mup-preview-fallback]');
      this._priceCurrent = this.querySelector('[data-mup-price-current]');
      this._priceCompare = this.querySelector('[data-mup-price-compare]');
      this._variantSummary = this.querySelector('[data-mup-variant-summary]');
      this._changeBtn = this.querySelector('[data-mup-change]');
      this._accordion = this.querySelector('[data-mup-accordion]');
      this._errorEl = this.querySelector('[data-mup-error]');
      this._availabilityErrorEl = this.querySelector('[data-mup-availability-error]');
      this._discountSummary = this.querySelector('[data-mup-discount-summary]');
      this._discountSummaryLabel = this.querySelector('[data-mup-discount-summary-label]');
      this._discountSummaryOriginal = this.querySelector('[data-mup-discount-summary-original]');
      this._discountSummaryFinal = this.querySelector('[data-mup-discount-summary-final]');

      this._slotEls = Array.from(this.querySelectorAll('[data-mup-slot]'));
      this._quantityPillEls = Array.from(this.querySelectorAll('[data-mup-quantity]'));

      this._state = {
        active: this._defaultActive,
        activeQuantity: this._defaultQuantity,
        // Sobald der Kunde manuell auf eine Stückzahl-Pille klickt, sperren
        // wir das Default-Mapping aus: er soll seine Wahl behalten, auch
        // wenn er danach noch die Hauptprodukt-Größe wechselt.
        manualQuantity: false,
        slots: Array.from({ length: this._maxQuantity }, () => ({
          variantId: null,
          options: this._optionNames.map(() => null),
          inheritsFromSlot1: true,
          manual: false,
        })),
      };

      // Single-Variant-Edge-Case: Variante intern direkt vorwählen für
      // ALLE slots, damit der Submit-Hook auch bei activeQuantity > 1
      // sofort eine variantId hat (Slot 2..N erben Slot 1).
      if (this._isSingleVariant && this._variants.length === 1) {
        const v = this._variants[0];
        const opts = this._optionNames.map((_, i) => v['option' + (i + 1)]);
        this._state.slots.forEach((slot) => {
          slot.variantId = v.id;
          slot.options = opts.slice();
        });
      } else if (this._autoSelectFirst) {
        this._preselectFirstAvailable();
      }

      this._bindToggle();
      this._bindRowActivation();
      this._bindChangeButton();
      this._bindQuantityPills();
      this._bindSlotToggles();
      this._bindOptionPills();
      this._bindSubmitListener();
      this._bindMainProductVariantWatcher();

      // Default-Mapping einmalig anwenden, falls die initial geladene
      // Hauptprodukt-Variante schon mit einem Mapping-Eintrag matched.
      this._applyDefaultMappingFromForm();

      this._render();
    }

    /* ---------- Init helpers ---------- */

    _preselectFirstAvailable() {
      const first = this._variants.find((v) => v.available) || this._variants[0];
      if (!first) return;
      const opts = this._optionNames.map((_, i) => first['option' + (i + 1)]);
      this._state.slots.forEach((slot) => {
        slot.variantId = first.id;
        slot.options = opts.slice();
      });
    }

    _findVariantByOptions(options) {
      if (!options || options.some((v) => v === null || v === undefined || v === '')) return null;
      return (
        this._variants.find((v) => {
          for (let i = 0; i < options.length; i++) {
            if (v['option' + (i + 1)] !== options[i]) return false;
          }
          return true;
        }) || null
      );
    }

    /* ---------- Event-Bindings ---------- */

    _bindToggle() {
      if (!this._toggle) return;
      this._toggle.addEventListener('change', () => {
        this._state.active = !!this._toggle.checked;

        // Falls aktiviert und noch keine Variante (Slot 1 leer): jetzt
        // automatisch vorbelegen.
        if (this._state.active && !this._state.slots[0].variantId && this._autoSelectFirst) {
          this._preselectFirstAvailable();
        }
        // Toggle aus → Akkordeon zuklappen, falls offen. Damit der
        // Bearbeiten-Button im disabled-State nicht auf einem offenen
        // Customizer hängt.
        if (!this._state.active && this._accordion && this._accordion.classList.contains('is-open')) {
          this._setAccordionOpen(false);
        }
        this._clearError();
        this._render();
      });
    }

    _bindChangeButton() {
      if (!this._changeBtn || !this._accordion) return;
      this._changeBtn.addEventListener('click', () => {
        // Disabled-State zusaetzlich gegen Programm-Klicks abgesichert:
        // visuell sperrt das CSS pointer-events, aber Custom-Element-
        // dispatchEvent oder Tastatur-Activate koennten trotzdem
        // durchkommen. _state.active ist die Source of Truth.
        if (!this._state.active) return;
        const open = this._accordion.classList.contains('is-open');
        this._setAccordionOpen(!open);
      });
    }

    /* Klick irgendwo im Block-Body (Toggle-Label, Bild, Headline,
       Variant-Summary, Preis, freier Bereich) aktiviert den Block,
       sodass der Kunde nicht exakt die kleine Checkbox treffen muss.
       Ausnahmen: der Anpassen-Button (öffnet das Akkordeon) und der
       native Toggle plus sein Label (haben ihren eigenen
       Click-/Change-Pfad und würden sonst doppelt feuern). */
    _bindRowActivation() {
      if (!this._row) return;
      this._row.addEventListener('click', (e) => {
        if (!e.target || typeof e.target.closest !== 'function') return;
        if (e.target.closest('[data-mup-change]')) return;
        if (e.target.closest('[data-mup-toggle], [data-mup-toggle-label]')) return;
        // Block toggeln (an oder aus, je nach aktuellem State).
        this._state.active = !this._state.active;
        if (this._toggle) this._toggle.checked = this._state.active;
        // Falls beim Aktivieren noch keine Variante in Slot 1 sitzt
        // (kein Auto-Select-First gewählt), jetzt nachholen.
        if (this._state.active && !this._state.slots[0].variantId && this._autoSelectFirst) {
          this._preselectFirstAvailable();
        }
        this._clearError();
        this._render();
      });
    }

    _bindQuantityPills() {
      this._quantityPillEls.forEach((pill) => {
        pill.addEventListener('click', () => {
          const qty = parseInt(pill.getAttribute('data-mup-quantity'), 10);
          if (!Number.isFinite(qty)) return;
          this._setActiveQuantity(qty, true);
        });
      });
    }

    _bindSlotToggles() {
      this._slotEls.forEach((slotEl) => {
        const toggle = slotEl.querySelector('[data-mup-slot-toggle]');
        const body = slotEl.querySelector('[data-mup-slot-body]');
        if (!toggle || !body) return;
        toggle.addEventListener('click', () => {
          const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
          body.hidden = wasOpen;
        });
      });
    }

    _bindOptionPills() {
      this.querySelectorAll('[data-mup-value]').forEach((pill) => {
        pill.addEventListener('click', () => {
          const optEl = pill.closest('[data-mup-option]');
          const slotEl = pill.closest('[data-mup-slot]');
          if (!optEl) return;
          const pos = parseInt(optEl.getAttribute('data-mup-option-position'), 10);
          const value = pill.getAttribute('data-mup-value-text');
          if (!Number.isFinite(pos) || value == null) return;
          // slot-1 wenn kein Slot-Wrapper drumherum (Single-Variant
          // Pfad rendert keinen Slot-Card-Container).
          const slotIndex = slotEl
            ? parseInt(slotEl.getAttribute('data-mup-slot-index'), 10) - 1
            : 0;
          this._setSlotOption(slotIndex, pos - 1, value);
        });
      });
    }

    _bindSubmitListener() {
      // Document-level capture, damit der Listener Aurora's Swap des
      // [data-product-block="buy_buttons"] überlebt (Form-Node wird bei
      // Variant-Change oft komplett ausgetauscht).
      document.addEventListener('click', this._onSubmitterClick, { capture: true });
      document.addEventListener('submit', this._onSubmit, { capture: true });
    }

    /* Reagiert auf Hauptprodukt-Variant-Wechsel UND auf Set-Tier-
       Wechsel im Set-Selector auf derselben PDP. Zwei Mechanismen
       parallel:

       1. Click-Capture auf alle Set-Selector-Tier-Tiles. Triggert
          sofort beim Klick (vor dem Polling-Tick) und sieht den
          neuen aria-checked-State im nächsten Animation-Frame.
       2. 250ms-Polling als Fallback für Variant-ID-Änderungen
          (Aurora's variant-picker liefert kein zuverlässiges Event)
          und als Sicherheitsnetz für edge cases, in denen der
          Click-Listener nicht gefeuert hat (z.B. Tier-Wechsel via
          Keyboard auf einer Set-Selector-Pille). */
    _bindMainProductVariantWatcher() {
      if (!this._productFormId) return;

      const readWatchValue = () => {
        const form = document.getElementById(this._productFormId);
        const variantInput = form
          ? (form.querySelector('[data-product-form-variant]')
            || form.querySelector('input[name="id"]'))
          : null;
        const variantId = variantInput ? String(variantInput.value || '') : '';
        const tierQty = this._getMainSetTierQuantity();
        return variantId + '|' + tierQty;
      };

      this._lastWatchedVid = readWatchValue();
      this._mainVariantPollTimer = setInterval(() => {
        const v = readWatchValue();
        if (v && v !== this._lastWatchedVid) {
          log('main variant / tier changed (poll)', { from: this._lastWatchedVid, to: v });
          this._lastWatchedVid = v;
          this._applyDefaultMappingFromForm();
        }
      }, 250);

      this._onSetSelectorTileClick = (e) => {
        if (!e.target || typeof e.target.closest !== 'function') return;
        const tile = e.target.closest('meluya-set-selector [data-msl-tier-index]');
        if (!tile) return;
        // Zwei Frames warten, damit der Set-Selector Zeit hat, sein
        // aria-checked + den state zu aktualisieren, bevor wir
        // auswerten.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const v = readWatchValue();
            log('set-selector tile click', { read: v });
            if (v !== this._lastWatchedVid) {
              this._lastWatchedVid = v;
            }
            this._applyDefaultMappingFromForm();
          });
        });
      };
      document.addEventListener('click', this._onSetSelectorTileClick, { capture: true });
    }

    /* Liest die quantity (1, 2, 4 ...) des aktuell aktiven Tiers im
       Set-Selector auf derselben PDP via data-msl-tier-quantity (das
       Liquid-Snippet rendert das Attribut direkt am Tile). Wenn kein
       Set-Selector da ist oder kein Tile aktiv, fällt es auf 1 zurück
       (entspricht "1 Set" / Single-Item-PDP). */
    _getMainSetTierQuantity() {
      const activeTile = document.querySelector(
        'meluya-set-selector [data-msl-tier-index][aria-checked="true"]'
      );
      if (!activeTile) return 1;
      const q = parseInt(activeTile.getAttribute('data-msl-tier-quantity'), 10);
      return Number.isFinite(q) && q > 0 ? q : 1;
    }

    disconnectedCallback() {
      if (this._mainVariantPollTimer) {
        clearInterval(this._mainVariantPollTimer);
        this._mainVariantPollTimer = null;
      }
      if (this._onSetSelectorTileClick) {
        document.removeEventListener('click', this._onSetSelectorTileClick, { capture: true });
        this._onSetSelectorTileClick = null;
      }
    }

    /* ---------- State-Updates ---------- */

    _setAccordionOpen(open) {
      if (!this._accordion) return;
      this._accordion.classList.toggle('is-open', open);
      if (this._changeBtn) {
        this._changeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      // Erste sichtbare Slot-Card automatisch aufklappen, damit der
      // Kunde nicht zwei Klicks braucht. Set-Selector macht's genauso.
      if (open) {
        const firstActiveSlot = this._slotEls.find((el) => !el.hidden);
        if (firstActiveSlot) {
          const toggle = firstActiveSlot.querySelector('[data-mup-slot-toggle]');
          const body = firstActiveSlot.querySelector('[data-mup-slot-body]');
          if (toggle && body) {
            toggle.setAttribute('aria-expanded', 'true');
            body.hidden = false;
          }
        }
      }
    }

    _setActiveQuantity(qty, manual) {
      const clamped = Math.max(1, Math.min(this._maxQuantity, qty));
      this._state.activeQuantity = clamped;
      if (manual) this._state.manualQuantity = true;
      // Sobald die Stückzahl auf > 1 wechselt und Slot 1 schon eine
      // Auswahl hat, propagieren wir die nach unten in alle erbenden
      // Slots, damit Slot 2..N nicht plötzlich leer dastehen.
      this._propagateSlot1();
      this._clearError();
      this._render();
    }

    _setSlotOption(slotIndex, optionIdx, value) {
      const slot = this._state.slots[slotIndex];
      if (!slot) return;
      slot.options = slot.options.slice();
      slot.options[optionIdx] = value;
      const variant = this._findVariantByOptions(slot.options);
      slot.variantId = variant ? variant.id : null;

      if (slotIndex === 0) {
        // Slot 1 ist die Master-Quelle. Manueller Klick lockt den
        // Auto-Sync vom Hauptprodukt aus, sodass der Kunde seine
        // bewusste Wahl nicht beim nächsten Variant-Wechsel verliert.
        slot.manual = true;
        // Änderungen propagieren in alle Slots die noch erben.
        this._propagateSlot1();
      } else {
        // Manueller Override im Slot 2..N: erbt nicht mehr.
        slot.inheritsFromSlot1 = false;
        slot.manual = true;
      }

      // Auto-Activate: erste manuelle Option-Auswahl schaltet den
      // Toggle scharf, damit das Add-on im Submit landet.
      if (!this._state.active && variant) {
        this._state.active = true;
        if (this._toggle) this._toggle.checked = true;
      }

      this._clearError();
      this._render();
    }

    _propagateSlot1() {
      const slot1 = this._state.slots[0];
      if (!slot1 || !slot1.variantId) return;
      for (let i = 1; i < this._state.slots.length; i++) {
        const s = this._state.slots[i];
        if (!s.inheritsFromSlot1 || s.manual) continue;
        s.variantId = slot1.variantId;
        s.options = slot1.options.slice();
      }
    }

    _applyDefaultMappingFromForm() {
      if (!this._mainVariants || this._mainVariants.length === 0) return;
      if (!this._productFormId) return;
      const form = document.getElementById(this._productFormId);
      if (!form) return;
      const input = form.querySelector('[data-product-form-variant]')
        || form.querySelector('input[name="id"]');
      if (!input) return;
      const variantId = parseInt(input.value, 10);
      if (!variantId) return;

      const variant = this._mainVariants.find((v) => v.id === variantId);
      if (!variant) return;

      // Sammle alle Option-Werte der aktuellen Hauptprodukt-Variante
      // (option1/option2/option3). Werden für beide Sync-Schritte
      // unten genutzt: Mengen-Mapping und Slot-1-Variant-Sync.
      const mainOptionValues = ['option1', 'option2', 'option3']
        .map((key) => (variant[key] != null ? String(variant[key]) : ''))
        .filter(Boolean);

      this._applyDefaultQuantityFromMainOptions(mainOptionValues);
      this._syncSlot1FromMainOptions(mainOptionValues);
    }

    _applyDefaultQuantityFromMainOptions(mainOptionValues) {
      if (this._state.manualQuantity) return;
      if (!this._defaultMapping || this._defaultMapping.length === 0) return;

      const haystack = new Set(mainOptionValues.map((v) => v.toLowerCase().trim()));
      const setTierQty = this._getMainSetTierQuantity();

      // Tier-Quantity (1 / 2 / 4) auf den passenden Slot-Eintrag
      // mappen. Wenn ein anderer Wert reinkommt (z.B. weil Marc
      // einen 3er-Tier hinzufügt), nutzen wir den nächst-niedrigeren
      // Slot-Eintrag als Best-Effort-Fallback.
      const pickFieldForTier = (entry) => {
        const at1 = parseInt(entry.atTier1, 10) || 0;
        const at2 = parseInt(entry.atTier2, 10) || 0;
        const at4 = parseInt(entry.atTier4, 10) || 0;
        if (setTierQty >= 4) return at4 || at2 || at1;
        if (setTierQty >= 2) return at2 || at1;
        return at1;
      };

      let matchedQty = null;
      for (const entry of this._defaultMapping) {
        const match = String(entry.match || '').toLowerCase().trim();
        if (!match) continue;
        if (haystack.has(match)) {
          const q = pickFieldForTier(entry);
          if (Number.isFinite(q) && q > 0) {
            matchedQty = Math.min(this._maxQuantity, q);
            break;
          }
        }
      }

      if (matchedQty != null && matchedQty !== this._state.activeQuantity) {
        // Nicht als manual markieren, sodass weitere Variant- oder
        // Tier-Wechsel das Mapping erneut anwenden können.
        this._setActiveQuantity(matchedQty, false);
      }
    }

    /* Spiegelt die Optionen der aktuellen Hauptprodukt-Variante in
       Slot 1 des Upsell-Blocks, indem für jede Upsell-Option geschaut
       wird, ob einer der Hauptprodukt-Werte als gültiger Wert dieser
       Option existiert (z.B. Hauptprodukt-Farbe "Beige" → Upsell-Farbe
       "Beige", Hauptprodukt-Größe-Kissenbezug "80x80" → Upsell-Größe
       "80x80"). Greift nur, solange der Kunde Slot 1 noch nicht
       manuell angefasst hat (slot1.manual === true sperrt). Slot 2..N
       bekommen die neuen Werte über _propagateSlot1 mit, wenn sie noch
       erben. */
    _syncSlot1FromMainOptions(mainOptionValues) {
      const slot1 = this._state.slots[0];
      if (!slot1 || slot1.manual) return;
      if (!this._variants || this._variants.length === 0) return;
      if (!this._optionNames || this._optionNames.length === 0) return;

      const mainSet = new Set(mainOptionValues.map((v) => v.toLowerCase().trim()));
      let changed = false;
      const newOptions = (slot1.options || []).slice();

      for (let i = 0; i < this._optionNames.length; i++) {
        const key = 'option' + (i + 1);
        // Mögliche Werte dieser Upsell-Option = unique Werte über
        // alle Upsell-Variants an dieser Position.
        const possibleValues = new Set(
          this._variants
            .map((v) => v[key])
            .filter((v) => v != null && v !== '')
            .map((v) => String(v))
        );
        // Suche den ersten Hauptprodukt-Wert, der auch ein gültiger
        // Upsell-Wert dieser Position ist.
        let matchedUpsellValue = null;
        for (const upVal of possibleValues) {
          if (mainSet.has(String(upVal).toLowerCase().trim())) {
            matchedUpsellValue = upVal;
            break;
          }
        }
        if (matchedUpsellValue != null && newOptions[i] !== matchedUpsellValue) {
          newOptions[i] = matchedUpsellValue;
          changed = true;
        }
      }

      if (!changed) return;
      slot1.options = newOptions;
      const variant = this._findVariantByOptions(newOptions);
      slot1.variantId = variant ? variant.id : null;
      this._propagateSlot1();
      this._clearError();
      this._render();
    }

    /* ---------- Rendering ---------- */

    _render() {
      this._renderActiveAttr();
      this._renderQuantityPills();
      this._renderSlots();
      this._renderPreview();
      this._renderPrice();
      this._renderVariantSummary();
      this._renderDiscountSummary();
      this._renderAvailabilityError();
    }

    /* Wenn Block-Discount aktiv und Prozent > 0: rabattierter Preis,
       sonst Original. Cents bleiben Integer (consistent zu Shopify), damit
       der durchgestrichene Compare-Preis und die summary nicht halbe Cent
       anzeigen. */
    _applyDiscount(price) {
      if (!this._discountActive || this._discountPercent <= 0) return price;
      return Math.round((price * (100 - this._discountPercent)) / 100);
    }

    _renderActiveAttr() {
      if (this._state.active) {
        this.setAttribute('data-mup-active', '');
      } else {
        this.removeAttribute('data-mup-active');
      }
      // Bearbeiten-Button disabled wenn Toggle nicht gesetzt — visuell
      // ausgegraut via CSS, nicht klickbar via disabled-Attribut.
      if (this._changeBtn) {
        if (this._state.active) {
          this._changeBtn.removeAttribute('disabled');
          this._changeBtn.removeAttribute('aria-disabled');
        } else {
          this._changeBtn.setAttribute('disabled', '');
          this._changeBtn.setAttribute('aria-disabled', 'true');
        }
      }
    }

    /* Live-Verfuegbarkeitspruefung. Wird vom Render-Cycle bei jedem
       State-Update gerufen (Toggle, Slot-Option-Change, Quantity-
       Pille). Pro aktivem Slot wird die zugehoerige Variante in
       _variants nachgeschlagen; ist sie nicht da oder nicht available,
       wird der Slot in der Fehler-Liste aufgenommen.

       Anzeige: Pro betroffenem Slot eine Zeile
         "Stueck N: <Farbe> · <Groesse> ist nicht verfuegbar."
       Stueck-Praefix kommt aus _labels.slotLabelPrefix (Schema-
       Setting). Option-Werte werden in der Reihenfolge der Produkt-
       Optionen mit · als Trenner zusammengefuegt.

       Verstecken: Wenn Toggle off, kein Fehler-Slot oder kein
       konfiguriertes Stueck (Slot ohne variantId), bleibt der
       Hinweis hidden. */
    _renderAvailabilityError() {
      if (!this._availabilityErrorEl) return;

      // Nur bei aktivem Block. Bei deaktiviertem Toggle keine Hinweise
      // — der User sieht den Customizer ja gar nicht.
      if (!this._state.active) {
        this._availabilityErrorEl.hidden = true;
        this._availabilityErrorEl.textContent = '';
        return;
      }

      const activeQty = this._state.activeQuantity;
      const slotPrefix = (this._labels && this._labels.slotLabelPrefix) || 'Stück';
      const lines = [];

      for (let i = 0; i < activeQty; i++) {
        const slot = this._state.slots[i];
        if (!slot) continue;

        // Slot ohne variantId: User hat noch nichts ausgewaehlt — kein
        // Verfuegbarkeits-Fehler, weil noch keine Auswahl.
        if (!slot.variantId) continue;

        const variant = this._variants.find((v) => v.id === slot.variantId);
        const isAvailable = !!(variant && variant.available);
        if (isAvailable) continue;

        // Option-Strings aus dem Slot-State: stehen in der Reihenfolge
        // der Produkt-Optionen (option1, option2, ...). Leere Eintraege
        // ueberspringen, damit "Beige · " nicht mit Trailing-Trenner
        // erscheint.
        const optionVals = (slot.options || []).filter(Boolean);
        const optionStr = optionVals.join(' · ');
        const slotLabel = slotPrefix + ' ' + (i + 1);
        if (optionStr) {
          lines.push(slotLabel + ': ' + optionStr + ' ist nicht verfügbar.');
        } else {
          lines.push(slotLabel + ': diese Variante ist nicht verfügbar.');
        }
      }

      if (lines.length === 0) {
        this._availabilityErrorEl.hidden = true;
        this._availabilityErrorEl.textContent = '';
        return;
      }

      // Mehrere Zeilen werden mit <br> getrennt, damit jede Stueck-
      // Meldung in eigener Zeile steht (statt Komma-getrennt durchlaufen).
      this._availabilityErrorEl.innerHTML = lines
        .map((l) => l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
        .join('<br>');
      this._availabilityErrorEl.hidden = false;
    }

    _renderQuantityPills() {
      const active = this._state.activeQuantity;
      this._quantityPillEls.forEach((pill) => {
        const v = parseInt(pill.getAttribute('data-mup-quantity'), 10);
        pill.setAttribute('aria-checked', v === active ? 'true' : 'false');
      });
    }

    /* Pro Slot-Card: visible/hidden je activeQuantity, Header-Summary
       updaten, Inheritance-Badge je inheritsFromSlot1 flippen, Pills
       aria-pressed gegen den Slot-State setzen, Verfügbarkeit pro Pill
       gegen die aktuelle Slot-Combo prüfen. */
    _renderSlots() {
      const activeQty = this._state.activeQuantity;
      this._slotEls.forEach((slotEl) => {
        const slotIndex = parseInt(slotEl.getAttribute('data-mup-slot-index'), 10);
        const isActive = slotIndex >= 1 && slotIndex <= activeQty;
        slotEl.hidden = !isActive;
        if (!isActive) return;
        const slot = this._state.slots[slotIndex - 1];
        if (!slot) return;

        // Slot-Summary
        const summaryEl = slotEl.querySelector('[data-mup-slot-summary]');
        if (summaryEl) {
          const inherited = slotIndex > 1 && slot.inheritsFromSlot1;
          if (inherited) {
            summaryEl.textContent = this._labels.inheritanceHint || 'wie Stück 1';
            summaryEl.classList.add('is-inherited');
          } else {
            summaryEl.textContent = (slot.options || []).filter(Boolean).join(' · ');
            summaryEl.classList.remove('is-inherited');
          }
        }

        // Inheritance-Badge: nur sichtbar wenn slot 2+ und noch erbt
        const badge = slotEl.querySelector('[data-mup-inheritance-badge]');
        if (badge) {
          if (slotIndex === 1) {
            badge.hidden = true;
          } else {
            badge.hidden = !slot.inheritsFromSlot1;
          }
        }

        // Pills aria-pressed
        slotEl.querySelectorAll('[data-mup-option]').forEach((optEl) => {
          const pos = parseInt(optEl.getAttribute('data-mup-option-position'), 10) - 1;
          const current = slot.options ? slot.options[pos] : null;
          optEl.querySelectorAll('[data-mup-value]').forEach((pill) => {
            const v = pill.getAttribute('data-mup-value-text');
            pill.setAttribute('aria-pressed', v === current ? 'true' : 'false');
          });
        });

        // Pills Verfügbarkeit: primäre Option (Farbe) hat globale
        // Verfügbarkeit, andere Optionen matchen die aktuelle Slot-Combo.
        // Identisches Pattern wie im Set-Selector.
        slotEl.querySelectorAll('[data-mup-option]').forEach((optEl) => {
          const pos = parseInt(optEl.getAttribute('data-mup-option-position'), 10) - 1;
          const key = 'option' + (pos + 1);
          const optionName = (optEl.getAttribute('data-mup-option-name') || '').toLowerCase();
          const isPrimary = (this._primaryOptionNames || []).some((p) => optionName.includes(p));

          optEl.querySelectorAll('[data-mup-value]').forEach((pill) => {
            const val = pill.getAttribute('data-mup-value-text');
            let hasAvailable;
            if (isPrimary) {
              hasAvailable = this._variants.some((v) => v.available && v[key] === val);
            } else {
              hasAvailable = this._variants.some((v) => {
                if (!v.available) return false;
                if (v[key] !== val) return false;
                for (let i = 0; i < this._optionNames.length; i++) {
                  if (i === pos) continue;
                  const cur = slot.options ? slot.options[i] : null;
                  if (cur != null && v['option' + (i + 1)] !== cur) return false;
                }
                return true;
              });
            }
            if (hasAvailable) pill.removeAttribute('data-mup-unavailable');
            else pill.setAttribute('data-mup-unavailable', '');
          });
        });
      });
    }

    _renderPreview() {
      if (!this._previewImg) return;
      const variant = this._currentVariant();
      if (!variant || !variant.featured_image || !variant.featured_image.url) return;
      const url = variant.featured_image.url;
      if (this._previewImg.getAttribute('src') === url) return;
      this._previewImg.setAttribute('src', url);
      this._previewImg.removeAttribute('srcset');
      if (variant.featured_image.alt) {
        this._previewImg.setAttribute('alt', variant.featured_image.alt);
      }
    }

    /* Body-Preis: Gesamtsumme über alle aktiven Slots. Wenn alle Slots
       auf der gleichen Variante sitzen, zeigen wir zusätzlich die
       Multiplikation ("3 × 25,42€ = 76,26€"). Bei gemischter Auswahl
       reicht die reine Total-Summe. */
    _renderPrice() {
      if (!this._priceCurrent) return;
      const masterVariant = this._currentVariant();
      if (!masterVariant) return;

      const moneyFormat = this._labels.moneyFormat || '';
      const activeQty = this._state.activeQuantity;
      const activeSlots = this._state.slots.slice(0, activeQty);

      // Wenn der Variant noch nicht gewählt ist (Auto-select-first
      // ausgeschaltet, kein Mapping), zeigen wir nur den Master-Preis.
      const allHaveVariant = activeSlots.every((s) => s.variantId != null);
      const allSameVariant = allHaveVariant
        && activeSlots.every((s) => s.variantId === masterVariant.id);

      const slotPrices = activeSlots.map((s) => {
        if (!s.variantId) return masterVariant.price || 0;
        const v = this._variants.find((x) => x.id === s.variantId);
        return v ? (typeof v.price === 'number' ? v.price : 0) : 0;
      });
      const totalRaw = slotPrices.reduce((a, b) => a + b, 0);
      const totalDiscounted = slotPrices
        .map((p) => this._applyDiscount(p))
        .reduce((a, b) => a + b, 0);

      const discountActive = this._discountActive && this._discountPercent > 0;
      const totalForDisplay = discountActive ? totalDiscounted : totalRaw;
      const perUnitForDisplay = discountActive
        ? this._applyDiscount(masterVariant.price || 0)
        : (masterVariant.price || 0);

      // Body-Preis bleibt immer der Stückpreis, egal wie viele Stück
      // gewählt sind. Total / Multiplikation wäre an dieser Stelle zu
      // viel Information; der Kunde sieht das Total ohnehin im Cart-
      // Drawer nach dem Add-to-Cart. Die Multi-Stück-Information lebt
      // jetzt ausschließlich in den Quantity-Pills unter dem Block.
      this._priceCurrent.textContent = this._formatMoney(perUnitForDisplay, moneyFormat);

      // Compare-Preis (durchgestrichen) bleibt analog zum Body-Preis
      // immer Pro-Stück. Bei Discount-Active ist das der nicht-
      // rabattierte Single-Preis, sonst der Variant-Compare-At wenn er
      // größer als der aktuelle Preis ist.
      if (this._priceCompare) {
        if (this._priceMode === 'show_savings_only') {
          this._priceCompare.hidden = true;
        } else if (discountActive) {
          if (this._showStrikethrough) {
            this._priceCompare.textContent = this._formatMoney(masterVariant.price || 0, moneyFormat);
            this._priceCompare.hidden = false;
          } else {
            this._priceCompare.hidden = true;
          }
        } else {
          const compareAt = typeof masterVariant.compare_at_price === 'number'
            ? masterVariant.compare_at_price
            : 0;
          if (compareAt > masterVariant.price) {
            this._priceCompare.textContent = this._formatMoney(compareAt, moneyFormat);
            this._priceCompare.hidden = false;
          } else {
            this._priceCompare.hidden = true;
          }
        }
      }
    }

    /* Summary unter den Slot-Cards (offener Accordion-State). Zeigt
       Total-Original und Total-Rabattiert über alle aktiven Slots. */
    _renderDiscountSummary() {
      if (!this._discountSummary) return;
      const masterVariant = this._currentVariant();
      const visible =
        this._state.active &&
        this._discountActive &&
        this._discountPercent > 0 &&
        !!masterVariant;
      if (!visible) {
        this._discountSummary.hidden = true;
        return;
      }
      const moneyFormat = this._labels.moneyFormat || '';
      const activeQty = this._state.activeQuantity;
      const activeSlots = this._state.slots.slice(0, activeQty);
      const slotPrices = activeSlots.map((s) => {
        if (!s.variantId) return masterVariant.price || 0;
        const v = this._variants.find((x) => x.id === s.variantId);
        return v ? (typeof v.price === 'number' ? v.price : 0) : 0;
      });
      const original = slotPrices.reduce((a, b) => a + b, 0);
      const discounted = slotPrices
        .map((p) => this._applyDiscount(p))
        .reduce((a, b) => a + b, 0);
      if (this._discountSummaryLabel) {
        this._discountSummaryLabel.textContent = this._labels.discountLabel || '';
      }
      if (this._discountSummaryOriginal) {
        this._discountSummaryOriginal.textContent = this._showStrikethrough
          ? this._formatMoney(original, moneyFormat)
          : '';
        this._discountSummaryOriginal.hidden = !this._showStrikethrough;
      }
      if (this._discountSummaryFinal) {
        this._discountSummaryFinal.textContent = this._formatMoney(discounted, moneyFormat);
      }
      this._discountSummary.hidden = false;
    }

    _renderVariantSummary() {
      if (!this._variantSummary) return;
      const activeQty = this._state.activeQuantity;
      const slot1 = this._state.slots[0] || {};
      const opts = (slot1.options || []).filter(Boolean);
      const allSame = this._state.slots
        .slice(0, activeQty)
        .every((s) => s.variantId != null && s.variantId === slot1.variantId);
      // Body-Variant-Summary bleibt schlank ("Beige · 80x80"), egal wie
      // viele Stück gewählt sind. Die Stückzahl-Information lebt in den
      // Quantity-Pills unter dem Block; im Body würde sie nur
      // doppelmoppeln und nach 2-3 Stück die Zeile umbrechen.
      const head = allSame
        ? (opts.length ? opts.join(' · ') : '')
        : 'Gemischte Auswahl';
      this._variantSummary.textContent = head;
    }

    _currentVariant() {
      const slot1 = this._state.slots[0];
      if (!slot1 || !slot1.variantId) return null;
      return this._variants.find((v) => v.id === slot1.variantId) || null;
    }

    /* ---------- Submit-Hook ---------- */

    _onSubmitterClick(e) {
      const btn = e.target.closest && e.target.closest('button[type="submit"], input[type="submit"]');
      if (!btn) return;
      const form = btn.closest('form');
      if (!form || getFormId(form) !== this._productFormId) return;
      this._lastSubmitter = btn;
    }

    _onSubmit(e) {
      const form = (e.target && e.target.tagName === 'FORM')
        ? e.target
        : (e.target && e.target.closest && e.target.closest('form'));
      const formId = getFormId(form);
      log('submit event', {
        targetTag: e.target && e.target.tagName,
        formId: formId,
        expectedFormId: this._productFormId,
        match: !!(form && formId === this._productFormId),
        active: this._state.active,
        variantId: this._state.selectedVariantId,
      });
      if (!form || formId !== this._productFormId) return;

      // Block inaktiv: Original-Submit normal durchlaufen lassen.
      if (!this._state.active) {
        log('skip: block not active');
        return;
      }

      const activeQty = this._state.activeQuantity;
      const activeSlots = this._state.slots.slice(0, activeQty);
      log('submit: validating slots', {
        activeQty,
        slots: activeSlots.map((s) => ({
          variantId: s.variantId,
          options: s.options,
          inheritsFromSlot1: s.inheritsFromSlot1,
        })),
      });

      // Validierung: alle aktiven Slots müssen eine gültige Variante haben.
      const missing = activeSlots.find((s) => !s.variantId);
      if (missing) {
        log('submit: BLOCKED — slot ohne variantId', missing);
        e.preventDefault();
        e.stopImmediatePropagation();
        this._showError(this._labels.validationMissing || 'Bitte wähle Farbe und Größe für alle Stück.');
        if (this._accordion) this._setAccordionOpen(true);
        return;
      }

      // Verfuegbarkeitspruefung im Submit-Path bewusst entfernt:
      // _renderAvailabilityError zeigt den Hinweis live im Header-
      // Bereich, der User sieht das auch ohne Submit. Bei einer
      // tatsaechlich nicht verfuegbaren Variante meldet das Shopify-
      // Backend zurueck, der Cart-Add laeuft dann in den catch-Branch
      // von _patternKSubmit und wird dort behandelt. Cart-Button
      // selbst bleibt unangetastet.
      log('submit: validation passed');

      // Aurora's nativen <product-form>-Handler vollstaendig
       // unterdruecken — wir machen den Cart-Add selbst und triggern
       // den Drawer manuell. Sonst rennt Aurora's Submit-Pfad parallel
       // zu unserem und der Cart-State wird inkonsistent.
      e.preventDefault();
      e.stopImmediatePropagation();
      this._patternKSubmit(form, e.submitter || this._lastSubmitter);
    }

    /* Zentrale Stelle für das Cart-Add-Payload. Wenn der Block den Discount-
       Toggle aktiv hat, hängen wir Line-Item-Properties an, die die Discount-
       Function (cart.lines.discounts.generate.run) im Backend ausliest:
         _meluya_upsell      = "true"   Marker, dass die Line aus dem Block kommt
         _meluya_upsell_pct  = "<int>"  Prozent, server-seitig auf 30 geclampt
       Underscore-Prefix macht beide Properties privat (für den Kunden im
       Cart unsichtbar). Bei discount_active=false werden keine Properties
       gesendet, damit die Function die Line gar nicht erst betrachtet. */
    buildAddItemPayload(variantId, quantity) {
      const payload = {
        id: variantId,
        quantity: Math.max(1, parseInt(quantity, 10) || 1),
      };
      if (this._discountActive) {
        payload.properties = {
          _meluya_upsell: 'true',
          _meluya_upsell_pct: String(this._discountPercent),
        };
      }
      return payload;
    }

    /* Bündelt active Slots zu Cart-Items. Identische {variantId, properties}
       werden zu einer Cart-Zeile mit quantity N gemerged, sodass der
       Standard-Fall "N gleich konfigurierte Stücke" als eine saubere
       Drawer-Zeile erscheint. Slots mit individuellen Optionen bleiben
       jeweils eigene Zeilen. */
    _buildItemsForCart() {
      const activeQty = this._state.activeQuantity;
      const activeSlots = this._state.slots.slice(0, activeQty);
      const groups = new Map();
      activeSlots.forEach((slot) => {
        const item = this.buildAddItemPayload(slot.variantId, 1);
        const key = item.id + '|' + JSON.stringify(item.properties || {});
        if (groups.has(key)) {
          groups.get(key).quantity += 1;
        } else {
          groups.set(key, { ...item });
        }
      });
      return Array.from(groups.values());
    }

    /* Pattern K — atomic Single-Call Multi-Item-Add.

       Backend-Setup (Mai 2026): Die alte Single-Function mit beiden
       Discount-Pfaden (Property + Bundle-Tier) hatte einen Multi-Item-
       Eval-Race, der Properties bei Multi-Item-Adds verwarf. Die
       Function wurde in zwei separate Extensions
       meluya-upsell-discount und meluya-bundle-discount aufgeteilt;
       jede evaluiert genau einen Pfad und beide stacken via
       combinesWith=ALL. Damit ist Pattern K als atomarer Pfad clean.

       Spec:
         1. preventDefault + stopImmediatePropagation auf Submit-Event
            (passiert in _onSubmit). stopImmediatePropagation ist
            Pflicht weil Aurora's <product-form>-Listener im Bubble-
            Phase haengt und sonst parallel feuern wuerde.
         2. fetch POST /cart/add.js, Content-Type + Accept
            application/json, Body {items: [main, upsell]}, properties
            als nested Object am Upsell-Item, KEIN sections im Call.
         3. log('cart-add response', JSON.stringify(data, null, 2))
            mit JSON.stringify fuer die Diagnostik (nur mit ?mup_debug=1).
            Beides muss da sein: Hauptprodukt-Line mit discounts:[Bundle-Rabatt..]
            und Upsell-Line mit discounts:[Upsell-Rabatt..].
         4. fetch GET /?sections=<cartId> — separater Call holt das
            Drawer-HTML gegen den final allokierten Cart-State, sodass
            die Discounts im UI sichtbar sind.
         5. cartDrawer.purchaseHandler(parseHTML(html), {}) oeffnet
            den Drawer einmal mit korrektem Endzustand.

       Spinner via _setSubmitLoading deckt Step 2 bis 4 ab. */
    async _patternKSubmit(form, submitter) {
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionId = cartDrawer && cartDrawer.dataset
        ? cartDrawer.dataset.sectionId
        : null;
      log('pattern K: start', { sectionId });
      this._setSubmitLoading(submitter, true);

      try {
        const items = this._buildAtomicItems(form);
        if (items.length === 0) {
          throw { description: 'no items to add' };
        }

        // Step 2: atomic /cart/add.js JSON {items}, kein sections.
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: items }),
        });
        if (!addRes.ok) {
          const errData = await addRes.json().catch(() => null);
          throw errData || { description: 'cart/add failed' };
        }
        const addData = await addRes.json();

        // Step 3: Cart-Response detailliert loggen — laeuft ueber log(), ist
        // also nur mit ?mup_debug=1 bzw. localStorage.mupDebug='1' sichtbar
        // und nicht bei jedem Kunden-Kauf in der Konsole. Zum Pruefen der
        // Rabatte muss beides da sein: Hauptprodukt-Line mit
        // discounts:[Bundle-Rabatt] und Upsell-Line mit
        // discounts:[Upsell-Rabatt]. Die zwei separaten Function-Extensions
        // (meluya-upsell-discount / meluya-bundle-discount) allocaten
        // unabhaengig.
        try {
          log('cart-add response', JSON.stringify(addData, null, 2));
        } catch (e) { /* ignore */ }

        // Step 4: Section-HTML separat fetchen — der zweite Call rendert
        // das Drawer-HTML gegen den final allokierten Cart-State, sodass
        // beide Discounts im UI sichtbar sind.
        let sectionHtml = null;
        if (sectionId) {
          const sectionsRes = await fetch(
            '/?sections=' + encodeURIComponent(sectionId),
            { headers: { Accept: 'application/json' } }
          );
          if (sectionsRes.ok) {
            const sectionsJson = await sectionsRes.json().catch(() => null);
            if (sectionsJson && typeof sectionsJson[sectionId] === 'string') {
              sectionHtml = sectionsJson[sectionId];
            }
          }
          log('pattern K: section fetch', { ok: sectionsRes.ok, hasHtml: !!sectionHtml });
        }

        // Step 5: cartDrawer.purchaseHandler. Wenn kein Drawer im DOM
        // (Cart-Page-Modus / Notification-Popup-Setup) oder kein
        // Section-HTML kam: redirect /cart als zuverlaessiger Fallback.
        if (cartDrawer && sectionHtml && typeof cartDrawer.purchaseHandler === 'function') {
          const node = new DOMParser().parseFromString(sectionHtml, 'text/html').body;

          // Aurora's Cart-Item-Animation laeuft in zwei Stufen:
          //   1. CartDrawerItemsBlock.handleCartDrawerUpdate setzt
          //      is-new auf das cart-item BEVOR es gemountet wird,
          //      sodass mountComponent die height auf 0 setzt (slide-
          //      in startposition). Trigger: parsedState.quantity===1.
          //   2. CartDrawerItemsBlock.handleCartDrawerPurschase ruft
          //      item.showAsNew() (slide-in animation) UND
          //      item.showCheckmark() (Haken-Animation 2.5s).
          //      showAsNew nur bei quantity===1, showCheckmark immer
          //      wenn parsedState.id matcht und das item gefunden wird.
          //
          // Beim nativem Aurora-Submit ist parsedState die /cart/add.js
          // Response — ein Single-Item-Object {id, quantity, ...}.
          // Beim Multi-Item-POST gibt es keinen kanonischen "this is
          // the new line"-Marker. Wir nehmen das letzte addData.items
          // (== Hauptprodukt durch unsere _buildAtomicItems Reihen-
          // folge), uebergeben es als parsedState — damit Aurora
          // seine native Animation auf der Hauptprodukt-Line laufen
          // laesst. Fuer die uebrigen Lines (Upsell-Items) iterieren
          // wir nach dem purchaseHandler manuell und rufen showCheck-
          // mark() direkt auf dem cart-item Custom Element auf, sodass
          // die Haken-Animation auch auf der Upsell-Line erscheint.
          const itemsArr = Array.isArray(addData.items) ? addData.items : [];
          const lastItem = itemsArr[itemsArr.length - 1];
          const parsedState = lastItem
            ? {
                id: lastItem.variant_id || lastItem.id,
                quantity: lastItem.quantity,
                key: lastItem.key,
              }
            : {};
          const lastVid = parsedState.id;

          await cartDrawer.purchaseHandler(node, parsedState);

          // Manuell showCheckmark auf den uebrigen frisch addierten
          // Lines (alle ausser lastItem, das hat Aurora's purchase-
          // Event schon animiert).
          try {
            itemsArr.forEach((it) => {
              const vid = it && (it.variant_id || it.id);
              if (!vid || vid === lastVid) return;
              const cartItemEl = cartDrawer.querySelector(
                'cart-item[data-variant-id="' + vid + '"]'
              );
              if (cartItemEl && typeof cartItemEl.showCheckmark === 'function') {
                cartItemEl.showCheckmark();
              }
            });
          } catch (e) {
            log('manual showCheckmark failed', e);
          }
        } else {
          window.location.assign('/cart');
        }
      } catch (err) {
        log('pattern K: error', err);
        this._showError(
          (err && err.description) ||
          (err && err.message) ||
          this._labels.validationUnavailable ||
          'Add to cart failed'
        );
      } finally {
        this._setSubmitLoading(submitter, false);
      }
    }

    /* Spinner-State auf dem Submit-Button waehrend aller Cart-Calls.
       Aurora's Setup folgt der aria-busy + .loading Konvention; wir
       setzen beides damit Theme-Styling und Accessibility-Tools
       gleich rangehen. Disabled bewusst nicht — Aurora's
       setLoading/setDisable greift schon vorm naechsten Submit, wir
       wollen das Form selbst nicht modifizieren. */
    _setSubmitLoading(submitter, on) {
      if (!submitter) return;
      try {
        if (on) {
          submitter.setAttribute('aria-busy', 'true');
          submitter.classList.add('loading');
        } else {
          submitter.removeAttribute('aria-busy');
          submitter.classList.remove('loading');
        }
      } catch (e) {}
      try {
        if (on) {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      } catch (e) {}
    }

    /* Baut das items-Array fuer Pattern K: Upsell-Slot-Items zuerst,
       Hauptprodukt zuletzt. Reihenfolge ist bewusst gewaehlt: Aurora's
       Cart-Drawer rendert Cart-Lines newest-first, das letzte items[N]
       erscheint also oben im Drawer. Damit das Hauptprodukt (Bett-
       waesche, primary purchase) oben steht, kommt es als items[last].
       properties aus dem Form-Markup landen am Hauptprodukt-Item,
       _meluya_upsell + _meluya_upsell_pct kommen aus _buildItemsForCart
       am Upsell-Item. */
    _buildAtomicItems(form) {
      let main = null;
      try {
        const fd = new FormData(form);
        const idRaw = fd.get('id');
        const qtyRaw = fd.get('quantity');
        const id = parseInt(idRaw, 10);
        if (Number.isFinite(id) && id > 0) {
          main = {
            id: id,
            quantity: parseInt(qtyRaw, 10) || 1,
          };
          const props = {};
          fd.forEach((v, k) => {
            const m = k.match(/^properties\[([^\]]+)\]$/);
            if (m && v !== '') props[m[1]] = v;
          });
          if (Object.keys(props).length) main.properties = props;
        }
      } catch (e) {
        log('form data extraction failed', e);
      }
      const upsells = this._buildItemsForCart();
      return main ? upsells.concat([main]) : upsells;
    }

    /* ---------- Error UI ---------- */

    _showError(msg) {
      if (!this._errorEl) return;
      this._errorEl.textContent = msg || '';
      this._errorEl.hidden = !msg;
    }

    _clearError() {
      this._showError('');
    }

    /* ---------- Money formatting ---------- */

    _formatMoney(cents, format) {
      let formatted = null;
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        try {
          formatted = Shopify.formatMoney(cents, format);
        } catch (e) {
          /* fallback unten */
        }
      }
      if (formatted == null) {
        const amount = (cents / 100).toFixed(2).replace('.', ',');
        formatted = amount + '€';
      }
      // Marc-Konvention aus dem Set-Selector: Currency direkt am Wert.
      return formatted.replace(/\s+(€|EUR)(?=\s*$)/, '$1');
    }
  }

  window.customElements.define('meluya-upsell-product', MeluyaUpsellProduct);
})();
